import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Ban, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { api, ghs } from "../../lib/api";

const VIEW = {
  success: { icon: CheckCircle2, color: "text-emerald-400", title: "Payment successful", text: "Your payment was verified and your access has been granted." },
  failed: { icon: XCircle, color: "text-red-400", title: "Payment failed", text: "The payment was not completed. You have not been given access and you can try again." },
  cancelled: { icon: Ban, color: "text-slate-300", title: "Payment cancelled", text: "You cancelled this payment. No access was granted." },
  pending: { icon: Clock, color: "text-amber-400", title: "Payment pending", text: "We're still waiting for confirmation from theteller. If you approved a Mobile Money prompt, this can take a minute." },
};

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const ref = params.get("transaction_id") || params.get("ref") || "";
  const [tx, setTx] = useState(null);
  const [error, setError] = useState("");
  const tries = useRef(0);

  const verify = useCallback(async () => {
    try {
      const { data } = await api.post(`/payments/${ref}/verify`);
      setTx(data);
      if (data.status === "pending" && tries.current < 6) {
        tries.current += 1;
        setTimeout(verify, 4000);
      }
    } catch { setError("We couldn't find this payment."); }
  }, [ref]);

  useEffect(() => { if (ref) verify(); else setError("Missing payment reference."); }, [ref, verify]);

  if (error) return <div className="mx-auto max-w-md text-center text-slate-300" data-testid="payment-callback-error">{error} <Link to="/payments" className="text-emerald-300">View payments</Link></div>;
  if (!tx) return <div className="grid min-h-[50vh] place-items-center text-slate-400" data-testid="payment-verifying"><span className="flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Verifying payment with theteller…</span></div>;

  const v = VIEW[tx.status];
  const target = tx.item_type === "product" ? "/purchases" : "/predictions";
  return (
    <div className="mx-auto max-w-md">
      <div className="card-rx rise p-8 text-center" data-testid={`payment-result-${tx.status}`}>
        <v.icon size={52} className={`mx-auto ${v.color}`} />
        <h1 className="mt-5 text-2xl font-bold text-white">{v.title}</h1>
        <p className="mt-2 text-sm text-slate-400">{v.text}</p>
        <div className="mt-6 space-y-2 rounded-xl bg-white/[0.03] p-4 text-left text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Item</span><span className="text-slate-200">{tx.item_name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-mono-rx text-slate-200">{ghs(tx.amount_minor)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Reference</span><span className="font-mono-rx text-slate-200" data-testid="payment-result-ref">{tx.reference}</span></div>
        </div>
        <div className="mt-7 grid gap-3">
          {tx.status === "success" && <Link to={target} className="btn-primary" data-testid="payment-result-open-btn">{tx.item_type === "product" ? "Go to My Purchases" : "Go to predictions"}</Link>}
          {tx.status === "pending" && <button onClick={() => { tries.current = 0; verify(); }} className="btn-primary" data-testid="payment-recheck-btn">Check again</button>}
          {["failed", "cancelled"].includes(tx.status) && <Link to={`/checkout/${tx.item_type}/${tx.item_id}`} className="btn-primary" data-testid="payment-retry-btn">Try again</Link>}
          <Link to="/payments" className="btn-ghost">Payment history</Link>
        </div>
      </div>
    </div>
  );
}
